import type { Graphics as PixiGraphics } from "pixi.js";
import { Graphics } from "@pixi/react";

/** Gold / yellow star fill for booking favorites on the floor plan */
const STAR_FILL = 0xffd700;
const STAR_STROKE = 0xc9a000;

export function drawFivePointStarAtOrigin(
  g: PixiGraphics,
  outerRadius: number,
  innerRadius: number
): void {
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i < 10; i += 1) {
    const r = i % 2 === 0 ? outerRadius : innerRadius;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    points.push({ x: r * Math.cos(a), y: r * Math.sin(a) });
  }
  g.beginFill(STAR_FILL);
  g.lineStyle(Math.max(1, outerRadius * 0.08), STAR_STROKE);
  g.drawPolygon(points);
  g.endFill();
}

interface FavoriteStarGraphicProps {
  /** Scales the star size (places: own scale; zones: dampened vs. `warnScale` in `ZoneContainer`) */
  starScale?: number;
  eventMode?: "none" | "static";
}

/** Unscaled outer radius; multiplied by `starScale` in `FavoriteStarGraphic` */
export const FAVORITE_STAR_OUTER_BASE = 36;

/**
 * Five-point star centered at local (0, 0). Parent should position this container
 * (e.g. zone centroid or place top-right inset).
 */
export function FavoriteStarGraphic({
  starScale = 0.7,
  eventMode = "none"
}: Readonly<FavoriteStarGraphicProps>) {
  const outer = FAVORITE_STAR_OUTER_BASE * starScale;
  const inner = outer * 0.38;

  return (
    <Graphics
      data-testid="favorite-star-grp"
      eventMode={eventMode}
      draw={g => {
        g.clear();
        drawFivePointStarAtOrigin(g, outer, inner);
      }}
    />
  );
}
